import qs from 'qs'

import { ApiContentType } from '../../enums/apiContentType'
import { deleteV2Request, getRawRequest, getRequest, patchRequest, postV2Request, request } from '../../utils/api'
import { CreateDocumentPayload, DmsDocument, DmsFolderContents, DmsFolderNode } from './types'

export const dmsPath = (organizationId: string, path: string) =>
  `/advisor-dms/organizations/${organizationId}/v1${path}`

export const fetchFolders = (organizationId: string, isCpa: boolean): Promise<DmsFolderNode[]> => {
  const queryParams = qs.stringify({ isCpa })
  return getRequest(`${dmsPath(organizationId, '/folders')}?${queryParams}`)
}

export const fetchFolderContents = (organizationId: string, folderId: string): Promise<DmsFolderContents> =>
  getRequest(dmsPath(organizationId, `/folders/${folderId}/contents`))

export const fetchDocumentById = (organizationId: string, documentId: string): Promise<DmsDocument> =>
  getRequest(dmsPath(organizationId, `/documents/${documentId}`))

export interface RenameFolderPayload {
  organizationId: string
  folderId: string
  name: string
}

export const renameFolder = ({ organizationId, folderId, name }: RenameFolderPayload): Promise<DmsFolderNode> =>
  patchRequest(dmsPath(organizationId, `/folders/${folderId}`), { name })

export interface MoveFolderPayload {
  organizationId: string
  folderId: string
  parentFolderId: string | null
}

export const moveFolder = ({ organizationId, folderId, parentFolderId }: MoveFolderPayload): Promise<DmsFolderNode> =>
  postV2Request({
    endpoint: dmsPath(organizationId, `/folders/${folderId}/move`),
    payload: { parentFolderId },
    options: { contentType: ApiContentType.ApplicationJSON },
  })

export interface DeleteFolderPayload {
  organizationId: string
  folderId: string
}

export const deleteFolder = ({ organizationId, folderId }: DeleteFolderPayload): Promise<void> =>
  deleteV2Request({ endpoint: dmsPath(organizationId, `/folders/${folderId}`) })

export interface CreateDocumentArgs {
  organizationId: string
  payload: CreateDocumentPayload
}

export const createDocument = ({ organizationId, payload }: CreateDocumentArgs): Promise<DmsDocument> => {
  const formData = new FormData()
  formData.append('file', payload.file)
  formData.append('externalUserId', payload.externalUserId)
  formData.append('origin', String(payload.origin))
  if (payload.folderId) {
    formData.append('folderId', payload.folderId)
  }
  if (payload.metadata) {
    formData.append('metadata', JSON.stringify(payload.metadata))
  }

  return request(
    dmsPath(organizationId, '/documents'),
    { method: 'POST', body: formData },
    { withAuth: true, skipContentType: true },
  )
}

export interface RenameDocumentPayload {
  organizationId: string
  documentId: string
  name: string
}

export const renameDocument = ({ organizationId, documentId, name }: RenameDocumentPayload): Promise<DmsDocument> =>
  patchRequest(dmsPath(organizationId, `/documents/${documentId}`), { name })

export interface MoveDocumentPayload {
  organizationId: string
  documentId: string
  folderId: string
}

export const moveDocument = ({ organizationId, documentId, folderId }: MoveDocumentPayload): Promise<DmsDocument> =>
  patchRequest(dmsPath(organizationId, `/documents/${documentId}/folder`), { folderId })

export interface DeleteDocumentPayload {
  organizationId: string
  documentId: string
}

export const deleteDocument = ({ organizationId, documentId }: DeleteDocumentPayload): Promise<void> =>
  deleteV2Request({ endpoint: dmsPath(organizationId, `/documents/${documentId}`) })

export const fetchDocumentPreviewBlob = async (
  organizationId: string,
  documentId: string,
  workflowId?: string,
): Promise<Blob> => {
  const workflowQuery = workflowId ? `?workflow_id=${encodeURIComponent(workflowId)}` : ''
  const path = dmsPath(organizationId, `/documents/${documentId}/download${workflowQuery}`)
  const response = await getRawRequest(path, { Accept: 'application/octet-stream' })

  return response.blob()
}
